import { TeamFlags, JoinTypes, Plans, ProfileFlags, PlaybookTabs, type JoinType } from '@labradorsports/constants';
import { getFolderParent, getTeamUserRole, isUserAdmin, maskObjectPaths, teamHasFlag } from '@labradorsports/utils';

import { ProfileSetupStepType } from '../shared/interface/index.js';

import { authApi } from './auth/api.js';
import { AuthState, authDefaultState } from './auth/index.js';
import { BillingState, billingDefaultState } from './billing/index.js';
import { communityApi } from './community/api.js';
import { CommunityState, communityDefaultState } from './community/index.js';
import { evaluationsApi } from './evaluations/api.js';
import { EvaluationsState } from './evaluations/index.js';
import { MainState, mainDefaultState } from './main/index.js';
import { MessagingState, messagingDefaultState } from './messaging/index.js';
import { PlayEditorState, playEditorDefaultState } from './play-editor/index.js';
import { playbookApi } from './playbook/api.js';
import { PlaybookState, playbookDefaultState } from './playbook/index.js';
import { RouterState, routerDefaultState } from './router/index.js';
import { scheduleApi } from './schedule/api.js';
import { ScheduleState, scheduleDefaultState } from './schedule/index.js';
import { ScoutingReportState, scoutingReportDefaultState } from './scouting-report/index.js';
import { teamsApi } from './teams/api.js';
import { TeamsState, teamsDefaultState } from './teams/index.js';

export interface RootState {
    api: any;
    site: SiteSpec;
    auth: AuthState;
    main: MainState;
    messaging: MessagingState;
    playbook: PlaybookState;
    teams: TeamsState;
    schedule: ScheduleState;
    playEditor: PlayEditorState;
    billing: BillingState;
    scoutingReport: ScoutingReportState;
    community: CommunityState;
    evaluations: EvaluationsState;
    router: RouterState;
}

export const defaultState = {
    auth: authDefaultState,
    main: mainDefaultState,
    messaging: messagingDefaultState,
    playbook: playbookDefaultState,
    teams: teamsDefaultState,
    schedule: scheduleDefaultState,
    playEditor: playEditorDefaultState,
    billing: billingDefaultState,
    scoutingReport: scoutingReportDefaultState,
    community: communityDefaultState,
    router: routerDefaultState,
};

export const Selectors = {
    profileLoaded(state: RootState) {
        return state.auth.profileLoaded;
    },

    accountFormError(state: RootState) {
        return state.auth.accountFormError;
    },

    loginEmail(state: RootState) {
        return state.auth.loginEmail;
    },

    user(state: RootState) {
        return state.auth.user;
    },

    uid(state: RootState) {
        return Selectors.user(state)?.uid;
    },

    shareConfig(state: RootState) {
        return state.playbook.shareConfig;
    },

    offline(state: RootState) {
        return state.main.offline;
    },

    focused(state: RootState) {
        return state.main.focused;
    },

    currentPlaybookId(state: RootState, playbookType: string) {
        const myPlaybook = Selectors.myPlaybook(state);
        const teamPlaybook = Selectors.teamPlaybook(state);
        const isMyPlaybook = playbookType === PlaybookTabs.PERSONAL;

        return isMyPlaybook ? myPlaybook?.id : teamPlaybook?.id;
    },

    play(state: RootState) {
        return state.playEditor.play;
    },

    playbook(state: RootState) {
        return state.playbook;
    },

    playbookId(state: RootState) {
        const selector = playbookApi.endpoints.loadMyPlaybook.select();

        return selector(state).data?.id;
    },

    playId(state: RootState) {
        return RouterSelectors.playId(state);
    },

    viewEventId(state: RootState) {
        return RouterSelectors.viewEventId(state);
    },

    viewPostId(state: RootState) {
        return RouterSelectors.viewPostId(state);
    },

    teams(state: RootState) {
        const teamsSelector = teamsApi.endpoints.fetchTeams.select();

        return teamsSelector(state).data ?? {};
    },

    myPlaybook(state: RootState) {
        const selector = playbookApi.endpoints.loadMyPlaybook.select();

        return selector(state).data;
    },

    teamPlaybook(state: RootState) {
        const activeTeam = RouterSelectors.activeTeam(state);
        const selector = playbookApi.endpoints.loadTeamPlaybook.select({
            activeTeam,
        });

        return selector(state).data;
    },

    currentFolders(state: RootState, playbookType: string) {
        const myPlaybook = Selectors.myPlaybook(state);
        const teamPlaybook = Selectors.teamPlaybook(state);
        const { folders: myFolders } = myPlaybook ?? {};
        const { folders: teamFolders } = teamPlaybook ?? {};

        return playbookType === PlaybookTabs.PERSONAL ? myFolders : teamFolders;
    },

    parentFolderId(state: RootState, playbookType: string) {
        const folders = Selectors.currentFolders(state, playbookType);

        return getFolderParent(folders, RouterSelectors.folderId(state))?.id;
    },

    currentTeam(state: RootState): any {
        const activeTeam = RouterSelectors.activeTeam(state);
        const teams = Selectors.allTeams(state);

        if (activeTeam) {
            return teams.find((tm) => tm.id === activeTeam);
        }

        return null;
    },

    currentProgram(state: RootState): any {
        const currentTeam = Selectors.currentTeam(state);

        if (currentTeam) {
            return Selectors.teams(state).programs?.find((prog) => prog.id === currentTeam.programId);
        }

        return null;
    },

    currentBranding(state: RootState): any {
        const currentProgram = Selectors.currentProgram(state);

        if (currentProgram && currentProgram.plan !== Plans.BASIC) {
            const { colorPrimary, colorSecondary } = currentProgram;

            return {
                colorPrimary,
                colorSecondary,
            };
        }

        return null;
    },

    currentPlan(state: RootState): string {
        const currentTeam = Selectors.currentTeam(state);

        if (currentTeam) {
            return currentTeam.plan;
        }

        return null;
    },

    currentSport(state: RootState): string {
        const currentTeam = Selectors.currentTeam(state);
        return currentTeam?.sport;
    },

    teamPaid(state: RootState): boolean {
        const currentTeam = Selectors.currentTeam(state);

        if (currentTeam) return currentTeam.paid;

        return false;
    },

    teamViewable(state: RootState): boolean {
        const teamPaid = Selectors.teamPaid(state);
        const userRole = Selectors.currentUserTeamRole(state);

        return teamPaid || userRole === JoinTypes.ADMIN;
    },

    teamHasFlag(state: RootState, flag: string, teamOverride?: any): boolean {
        const team = teamOverride ?? Selectors.currentTeam(state);

        return teamHasFlag(team, flag);
    },

    currentUserTeamRole(state: RootState): JoinType {
        const team = Selectors.currentTeam(state);

        if (state.auth.user && team) {
            const { uid } = state.auth.user;

            return getTeamUserRole(team, uid);
        }

        return null;
    },

    currentUserTeamRoleIsCoach(state: RootState): boolean {
        const teamRole = Selectors.currentUserTeamRole(state);

        return ([JoinTypes.ADMIN, JoinTypes.COACH] as string[]).includes(teamRole);
    },

    currentUserProgramRole(state: RootState): JoinType {
        const program = Selectors.currentProgram(state);

        if (state.auth.user && program) {
            const isAdmin = isUserAdmin(program, state.auth.user.uid);

            return isAdmin ? JoinTypes.ADMIN : null;
        }

        return null;
    },

    currentUserIsProgramAdmin(state: RootState): boolean {
        const role = Selectors.currentUserProgramRole(state);

        return role === JoinTypes.ADMIN;
    },

    currentUserIsAdmin(state: RootState): boolean {
        if (state.auth.user) {
            const { uid } = state.auth.user;
            const allTeams = Selectors.allTeams(state);

            return allTeams.length > 0 && allTeams.some((team) => getTeamUserRole(team, uid) === JoinTypes.ADMIN);
        }

        return false;
    },

    currentUserIsPlayer(state: RootState): boolean {
        if (state.auth.user) {
            const { uid } = state.auth.user;
            const allTeams = Selectors.allTeams(state);
            return allTeams.length > 0 && allTeams.every((team) => getTeamUserRole(team, uid) === JoinTypes.PLAYER);
        }

        return false;
    },

    someTeamsPaid(state: RootState): boolean {
        return state.auth.user && Selectors.allTeams(state).some((team) => team.paid);
    },

    allTeamsPaid(state: RootState): boolean {
        return state.auth.user && Selectors.allTeams(state).every((team) => team.paid);
    },

    profile(state: RootState): any {
        const selector = authApi.endpoints.loadProfile.select();

        const { data: profile } = selector(state);

        return profile;
    },

    profileSettings(state: RootState): any {
        const uid = Selectors.uid(state);
        const selector = authApi.endpoints.getProfileSettings.select({ uid });

        const { data: profileSettings } = selector(state);

        return profileSettings;
    },

    profileFlags(state: RootState): any {
        const uid = Selectors.uid(state);
        const selector = authApi.endpoints.getProfileFlags.select({ uid });
        const { data: profileFlags } = selector(state);

        return profileFlags ?? {};
    },

    userHasFlag(state: RootState, flag: string): boolean {
        const flags = Selectors.profileFlags(state);

        return Boolean(flags?.[flag]);
    },

    basicDetails(state: RootState): any {
        return Selectors.profile(state)?.basicDetails ?? {};
    },

    playReadOnly(state: RootState): boolean {
        const { play } = state.playEditor;

        if (play) {
            const myPlaybook = Selectors.myPlaybook(state);
            // Can edit your own plays
            if (
                myPlaybook?.plays.findIndex((pl: any) => pl.id === play.id) !== -1 ||
                play.id.includes('__') ||
                play.new
            ) {
                return false;
            }

            const teamPlaybook = Selectors.teamPlaybook(state);
            // Can edit team plays if you are a coach and the team setting is enabled
            if (
                teamPlaybook?.shared.findIndex((pl: any) => pl.id === play.id) !== -1 &&
                Selectors.teamEditable(state) &&
                Selectors.teamHasFlag(state, TeamFlags.COACHES_CAN_EDIT_TEAM_PLAYS)
            ) {
                return false;
            }
        }

        // All other scenarios are read-only
        return true;
    },

    allTeams(state: RootState): any[] {
        const { ownedTeams, joinedTeams, pendingJoinTeams } = Selectors.teams(state);

        return (ownedTeams ?? []).concat(joinedTeams ?? []).concat(pendingJoinTeams ?? []);
    },

    editableTeams(state: RootState): any[] {
        if (!state.auth.user?.uid) return [];

        const { ownedTeams = [], joinedTeams = [] } = Selectors.teams(state);

        if (!joinedTeams) return ownedTeams;

        return joinedTeams.filter((tm: any) => tm.people[state.auth.user.uid] === JoinTypes.COACH).concat(ownedTeams);
    },

    teamEditable(state: RootState): boolean {
        const isCoach = Selectors.currentUserTeamRoleIsCoach(state);
        const isProgramAdmin = Selectors.currentUserIsProgramAdmin(state);

        return isCoach || isProgramAdmin;
    },

    currentRosterPlayer(state: RootState): any {
        const activeTeam = RouterSelectors.activeTeam(state);
        const rosterSelector = teamsApi.endpoints.retrieveRoster.select({ teamId: activeTeam });
        const { data: { roster } = {} } = rosterSelector(state);
        const rosterPlayerId = RouterSelectors.rosterPlayerId(state);

        return roster?.find((pl) => pl.id === rosterPlayerId);
    },

    canViewRoster(state: RootState): boolean {
        return (
            (Selectors.teamEditable(state) || Selectors.teamHasFlag(state, TeamFlags.PLAYERS_CAN_VIEW_TEAM_ROSTER)) &&
            (Selectors.teamPaid(state) || Selectors.currentUserTeamRole(state) === JoinTypes.ADMIN)
        );
    },

    activeEvaluation(state: RootState): any {
        const activeTeam = RouterSelectors.activeTeam(state);
        const playerId = RouterSelectors.rosterPlayerId(state);
        const evaluationsSelector = evaluationsApi.endpoints.getPlayerEvaluations.select({
            activeTeam,
            playerId,
        });
        const { data: { evaluations } = {} } = evaluationsSelector(state);

        return state.router.playerEvaluationId
            ? evaluations?.find((rating) => rating.id === state.router.playerEvaluationId)
            : evaluations?.[0];
    },

    currentEvent(state: RootState): any {
        const activeTeam = RouterSelectors.activeTeam(state);
        const scheduleSelector = scheduleApi.endpoints.loadSchedule.select({ activeTeam });
        const { data: events } = scheduleSelector(state);

        if (events) {
            return events.find((event) => event.id === RouterSelectors.viewEventId(state));
        }

        return null;
    },

    videoDownload(state: RootState, playId: string, playbookId?: string): any {
        return state.playbook.videoDownloads.find(
            (vd) => vd.playId === playId && (!playbookId || vd.playbookId === playbookId)
        );
    },

    communityPlay(state: RootState): any {
        const openPlayId = RouterSelectors.communityPlayId(state);
        const playSelector = communityApi.endpoints.getCommunityPlay.select({ id: openPlayId });
        const { data: play } = playSelector(state);

        return play;
    },

    loggableState(state: RootState) {
        const redactedPaths = [
            'api.mutations',
            'api.provided',
            'api.subscriptions',

            'api.queries.fetchBillingDetails(*).data.cards.*',
            'api.queries.getInvoices(*).data[*].receiptUrl',

            'api.queries.loadProfile(*).data.basicDetails.firstName',
            'api.queries.loadProfile(*).data.basicDetails.lastName',
            'api.queries.loadProfile(*).data.basicDetails.email',
            'api.queries.loadProfile(*).data.settings.notificationDevices',

            'playEditor.play.viewers.*.firstName',
            'playEditor.play.viewers.*.lastName',
            'playEditor.editHistory',
            'api.queries.loadMyPlay(*).data.viewers.*.firstName',
            'api.queries.loadMyPlay(*).data.viewers.*.lastName',
            'api.queries.loadMyPlaybook(*).data.plays[*].viewers',
            'api.queries.loadMyPlaybook(*).data.plays[*].viewers',
            'api.queries.loadMyPlaybook(*).data.plays[*].initial',
            'api.queries.loadMyPlaybook(*).data.plays[*].frames',
            'api.queries.loadMyPlaybook(*).data.plays[*].fieldPieces',

            'api.queries.loadSharedPlay(*).data.viewers.*.firstName',
            'api.queries.loadSharedPlay(*).data.viewers.*.lastName',
            'api.queries.loadTeamPlaybook(*).data.shared[*].viewers',
            'api.queries.loadTeamPlaybook(*).data.shared[*].viewers',
            'api.queries.loadTeamPlaybook(*).data.shared[*].initial',
            'api.queries.loadTeamPlaybook(*).data.shared[*].frames',
            'api.queries.loadTeamPlaybook(*).data.shared[*].fieldPieces',

            'api.queries.getCommunityPlaybook(*).data.plays[*].viewers',

            'api.queries.fetchTeams(*).data.ownedTeams[*].name',
            'api.queries.fetchTeams(*).data.joinedTeams[*].name',
            'api.queries.fetchTeams(*).data.programs[*].name',
            'api.queries.getTeamMembers(*).data.*',
            'api.queries.retrieveRoster(*).data.roster[*].email',
            'api.queries.retrieveRoster(*).data.roster[*].firstName',
            'api.queries.retrieveRoster(*).data.roster[*].lastName',

            'api.queries.retrieveRoster(*).data.playerRoster[*].email',
            'api.queries.retrieveRoster(*).data.playerRoster[*].firstName',
            'api.queries.retrieveRoster(*).data.playerRoster[*].lastName',
            'api.queries.retrieveRoster(*).data.contactMap',

            'api.queries.loadSchedule(*).data[*].location',
            'api.queries.loadSchedule(*).data[*].scoutingReport.opponentSummary',

            'api.queries.loadFeedPosts(*).data[*].content',
            'api.queries.loadFeedPosts(*).data[*].comments',

            'api.queries.loadScoutingReport(*).data.opponentSummary',
            'api.queries.loadScoutingReport(*).data.phases.*.content',
            'api.queries.loadScoutingReport(*).data.phases.*.filmUrl',
            'api.queries.loadScoutingReport(*).data.phases.*.personnel.*.description',
            'api.queries.loadScoutingReport(*).data.phases.*.personnel.*.matchup',
            'api.queries.loadScoutingReport(*).data.phases.*.personnel.*.name',
            'api.queries.loadScoutingReportViewerDetails(*).data',
            'api.queries.getNCAATeams(*).data',
            'api.queries.getNCAATeamData(*).data.schedule',
            'api.queries.getNCAATeamData(*).data.roster',
            'api.queries.getNCAATeamData(*).data.opponentStats',
            'api.queries.getNCAATeamData(*).data.theirOpponentStats',

            'auth',

            'profile.settings.notificationDevices',

            'playEditor.play.viewers.*.firstName',
            'playEditor.play.viewers.*.lastName',
            'playEditor.editHistory',
            'playEditor.PlayConfig',

            // This will be logged separately
            'main.actionLog',
        ];

        return maskObjectPaths(state, redactedPaths);
    },

    onboardingSteps(state: RootState): ProfileSetupStepType[] {
        const {
            auth: { user },
        } = state;
        const profileLoaded = Selectors.profileLoaded(state);
        const { ownedTeams = [], joinedTeams = [], programs = [] } = Selectors.teams(state);

        if (user && profileLoaded) {
            const commonSteps: ProfileSetupStepType[] = ['firstPlay', 'shareSocial', 'followSocial'];

            const extraSteps = ((): ProfileSetupStepType[] => {
                // Personal
                if (ownedTeams.length === 0 && joinedTeams.length === 0 && programs.length === 0) {
                    return [];
                }

                // Team Admin/Coach
                if (
                    ownedTeams.some((team) => team.plan !== Plans.BASIC) ||
                    joinedTeams.some((team) => team.plan !== Plans.BASIC && team.people[user.uid] === JoinTypes.COACH)
                ) {
                    return ['playbook', 'roster', 'schedule', 'downloadApp'];
                }

                // Basic Team Coach
                if (ownedTeams.length > 0 || joinedTeams.some((team) => team.people[user.uid] === JoinTypes.COACH)) {
                    return ['playbook', 'roster', 'downloadApp'];
                }

                // Player
                if (joinedTeams.some((team) => team.plan !== Plans.BASIC)) {
                    return ['viewPlaybook', 'viewSchedule', 'downloadApp'];
                }

                return ['viewPlaybook', 'downloadApp'];
            })();

            return [...commonSteps, ...extraSteps];
        }

        return [];
    },

    completedOnboardingSteps(state: RootState): any {
        const profileFlagMap: {
            [k in ProfileSetupStepType]: string;
        } = {
            firstPlay: ProfileFlags.CREATED_FIRST_PLAY,
            followSocial: ProfileFlags.FOLLOWED_SOCIAL,
            shareSocial: ProfileFlags.SHARED_SOCIAL,
            playbook: ProfileFlags.SHARED_PLAY_TEAM,
            roster: ProfileFlags.INVITED_TEAM,
            schedule: ProfileFlags.CREATED_FIRST_EVENT,
            viewPlaybook: ProfileFlags.VIEWED_PLAYBOOK,
            viewSchedule: ProfileFlags.VIEWED_SCHEDULE,
            downloadApp: ProfileFlags.DOWNLOADED_APP,
        };

        const profileFlags = Selectors.profileFlags(state);

        return Object.fromEntries(
            Selectors.onboardingSteps(state).map((step) => [step, !!profileFlags?.[profileFlagMap[step]]])
        );
    },

    findPlay(state: RootState, playId: string, prioritizeTeam = true): any {
        const myPlaybook = Selectors.myPlaybook(state);
        const teamPlaybook = Selectors.teamPlaybook(state);

        const fromTeam = (teamPlaybook?.shared ?? []).find((pl: any) => pl.id === playId);
        const fromMy = (myPlaybook?.plays ?? []).find((pl: any) => pl.id === playId);

        if (prioritizeTeam) {
            return fromTeam ?? fromMy;
        }

        return fromMy ?? fromTeam;
    },

    activeTeam(state) {
        return RouterSelectors.activeTeam(state);
    },

    selectedEvaluationTemplate(state: RootState) {
        const activeTeam = RouterSelectors.activeTeam(state);
        const selectedTemplateId = RouterSelectors.selectedEvaluationTemplateId(state);
        const templatesSelector = evaluationsApi.endpoints.getEvaluationTemplates.select({ activeTeam });
        const { data: templates = [] } = templatesSelector(state);

        return templates.find((template) => template.id === selectedTemplateId);
    },

    activeEvaluationTemplate(state: RootState) {
        const activeTeam = RouterSelectors.activeTeam(state);
        const templatesSelector = evaluationsApi.endpoints.getEvaluationTemplates.select({ activeTeam });
        const { data: templates = [] } = templatesSelector(state);

        return templates.find((template) => template.active);
    },
};

export const RouterSelectors = {
    activeTeam(state: RootState) {
        return state.router.activeTeam;
    },

    deeplink(state: RootState) {
        return state.router.deeplink;
    },

    invoiceLinkId(state: RootState) {
        return state.router.invoiceLinkId;
    },

    invoiceError(state: RootState) {
        return state.router.invoiceError;
    },

    communityPageNumber(state: RootState) {
        return state.router.communityPageNumber;
    },

    playId(state: RootState) {
        return state.router.playId;
    },

    folderId(state: RootState) {
        return state.router.folderId;
    },

    viewerDetailsPlayId(state: RootState) {
        return state.router.viewerDetailsPlayId;
    },

    rosterPlayerId(state: RootState) {
        return state.router.rosterPlayerId;
    },

    joinTeamCode(state: RootState) {
        return state.router.joinTeamCode;
    },

    joinTeamId(state: RootState) {
        return state.router.joinTeamId;
    },

    emailAction(state: RootState) {
        return state.router.emailAction;
    },

    editBillingEntityId(state: RootState) {
        return state.router.editBillingEntityId;
    },

    shownScheduleTab(state: RootState) {
        return state.router.shownScheduleTab;
    },

    viewEventId(state: RootState) {
        return state.router.viewEventId;
    },

    viewPracticeTemplateId(state: RootState) {
        return state.router.viewPracticeTemplateId;
    },

    rsvpUpdateStatus(state: RootState) {
        return state.router.rsvpUpdateStatus;
    },

    viewPostId(state: RootState) {
        return state.router.viewPostId;
    },

    viewDocumentAcksId(state: RootState) {
        return state.router.viewDocumentAcksId;
    },

    publicLinkId(state: RootState) {
        return state.router.publicLinkId;
    },

    communityPlayId(state: RootState) {
        return state.router.communityPlayId;
    },

    communityProfileId(state: RootState) {
        return state.router.communityProfileId;
    },

    communitySearchText(state: RootState) {
        return state.router.communitySearchText;
    },

    selectedEvaluationTemplateId(state: RootState) {
        return state.router.selectedEvaluationTemplateId;
    },

    playerEvaluationId(state: RootState) {
        return state.router.playerEvaluationId;
    },

    playbookFilter(state: RootState) {
        return state.router.playbookFilter;
    },

    playbookBrowseTab(state: RootState) {
        return state.router.playbookBrowseTab;
    },

    selectedFormationId(state: RootState) {
        return state.router.selectedFormationId;
    },

    selectedConceptId(state: RootState) {
        return state.router.selectedConceptId;
    },
};
